import { AlertCircle, Globe, HelpCircle, Monitor, Smartphone, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { CustomFooterItem } from '../types/footer';
import { CustomHeaderItem } from '../types/header';
import { getImageUrl } from '../utils';

// TypeScript interfaces
type ComponentData = CustomHeaderItem | CustomFooterItem;

interface ScreenOption {
    id: string;
    name: string;
    description: string;
    icon: React.ReactNode;
}

interface MakeDefaultModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (componentId: string, screens: string) => Promise<void>;
    componentData: ComponentData | null;
    componentType: 'header' | 'footer';
}

const MakeDefaultModal: React.FC<MakeDefaultModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    componentData,
    componentType
}) => {
    // Screen options
    const screenOptions: ScreenOption[] = [
        {
            id: 'desktop',
            name: 'Desktop Only',
            description: `Make this ${componentType} default for desktop screens (> 768px)`,
            icon: <Monitor className="w-5 h-5" />
        },
        {
            id: 'mobile',
            name: 'Mobile Only',
            description: `Make this ${componentType} default for mobile screens (≤ 768px)`,
            icon: <Smartphone className="w-5 h-5" />
        },
        {
            id: 'all',
            name: 'All Devices',
            description: `Make this ${componentType} default for all screen sizes`,
            icon: <Globe className="w-5 h-5" />
        }
    ];

    // State
    const [selectedScreens, setSelectedScreens] = useState<string>('all');
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isHelpOpen, setIsHelpOpen] = useState<boolean>(false);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setSelectedScreens('all');
            setIsProcessing(false);
        }
    }, [isOpen]);

    // Toggle screen selection
    const toggleScreenSelection = (screenId: string): void => {
        setSelectedScreens(screenId);
    };

    // Handle confirmation
    const handleConfirm = async (): Promise<void> => {
        if (!componentData) return;

        setIsProcessing(true);
        try {
            await onConfirm(componentData._id, selectedScreens);
            onClose();
        } catch (error) {
            console.error(`Error setting default ${componentType}:`, error);
        } finally {
            setIsProcessing(false);
        }
    };

    // If modal is closed or no component data is provided, don't render
    if (!isOpen || !componentData) return null;

    // Get the title case of component type
    const componentTypeTitle = componentType.charAt(0).toUpperCase() + componentType.slice(1);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                {/* Modal header */}
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-semibold">Make {componentTypeTitle} Default</h2>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-full hover:bg-gray-100"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Modal body */}
                <div className="p-4">
                    {/* Component preview */}
                    <div className="mb-4">
                        <p className="text-sm text-gray-500 mb-2">Selected {componentType}:</p>
                        <div className="flex items-center p-2 bg-gray-50 rounded-md">
                            <div className="w-12 h-8 overflow-hidden rounded border mr-3">
                                {componentData.images && (
                                    <img
                                        src={getImageUrl(componentData.images)}
                                        alt={componentData.name}
                                        className="w-full h-full object-cover"
                                    />
                                )}
                            </div>
                            <span className="font-medium">{componentData.name}</span>
                        </div>
                    </div>

                    {/* Help section */}
                    <div className="mb-4">
                        <div className="flex items-center justify-between">
                            <h3 className="font-medium mb-1">Select screen sizes</h3>
                            <button
                                onClick={() => setIsHelpOpen(!isHelpOpen)}
                                className="text-blue-600 hover:text-blue-800 flex items-center text-sm"
                            >
                                <HelpCircle className="w-4 h-4 mr-1" />
                                {isHelpOpen ? 'Hide help' : 'Need help?'}
                            </button>
                        </div>

                        {isHelpOpen && (
                            <div className="mb-4 p-3 bg-blue-50 rounded-md text-sm">
                                <div className="flex">
                                    <AlertCircle className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="mb-2">
                                            <strong>Desktop Only:</strong> This {componentType} will only be used as the default for visitors on desktop devices.
                                        </p>
                                        <p className="mb-2">
                                            <strong>Mobile Only:</strong> This {componentType} will only be used as the default for visitors on mobile devices.
                                        </p>
                                        <p>
                                            <strong>All Devices:</strong> This {componentType} will be used as the default regardless of device.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Screen options */}
                    <div className="space-y-3">
                        {screenOptions.map((option) => (
                            <div
                                key={option.id}
                                onClick={() => toggleScreenSelection(option.id)}
                                className={`p-3 border rounded-md cursor-pointer flex items-center ${selectedScreens.includes(option.id)
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                            >
                                <div className={`mr-3 p-1.5 rounded-full ${selectedScreens.includes(option.id) ? 'bg-blue-100' : 'bg-gray-100'
                                    }`}>
                                    {option.icon}
                                </div>
                                <div className="flex-grow">
                                    <h4 className="font-medium">{option.name}</h4>
                                    <p className="text-sm text-gray-500">{option.description}</p>
                                </div>
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedScreens.includes(option.id)
                                    ? 'border-blue-500 bg-blue-500'
                                    : 'border-gray-300'
                                    }`}>
                                    {selectedScreens.includes(option.id) && (
                                        <span className="text-white text-xs">✓</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Modal footer */}
                <div className="p-4 border-t flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-gray-700"
                        disabled={isProcessing}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                        disabled={isProcessing}
                    >
                        {isProcessing ? `Setting default...` : `Set as Default`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MakeDefaultModal;